// src/components/payrolls/PayrollTable.jsx

import React, { useState, useMemo, useEffect, useRef } from "react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { 
  markPayrollPaid, 
  markPayrollMassPaid, 
  preparePayroll, 
  checkPayroll, 
  approvePayroll,
  rejectPayroll 
} from "../../services/payroll.service";

// --- CHANGE 1: Add filterBudgetCode to props ---
const PayrollTable = ({ payrolls, refresh, filterPeriod, setFilterPeriod, filterBudgetCode }) => {
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingAction, setLoadingAction] = useState(null);
  const [feedbackMsg, setFeedbackMsg] = useState("");
  
  // Bulk Rejection State
  const [showBulkReject, setShowBulkReject] = useState(false);
  const [bulkRejectReason, setBulkRejectReason] = useState("");

  const payrollArray = Array.isArray(payrolls) ? payrolls : [];

  const formatCurrency = (amount) => {
    return `$${Number(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString();
  };

  const getUserInitials = (userObj) => {
    if (!userObj) return "-";
    const name = `${userObj.firstName || ""} ${userObj.lastName || ""}`;
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .substring(0, 2);
  };

  const availablePeriods = useMemo(() => {
    const periods = new Set();
    payrollArray.forEach((p) => {
      if (p.payrollPeriod?.year && p.payrollPeriod?.month) {
        const y = p.payrollPeriod.year;
        const m = String(p.payrollPeriod.month).padStart(2, '0');
        periods.add(`${y}-${m}`);
      }
    });
    return Array.from(periods).sort().reverse();
  }, [payrollArray]);

  // --- CHANGE 2: Update Filter Logic ---
  const filteredPayrolls = useMemo(() => {
    let result = payrollArray;
    
    // 1. Filter by Period
    if (filterPeriod) {
      result = result.filter((p) => {
        const py = p.payrollPeriod?.year;
        const pm = String(p.payrollPeriod?.month || "").padStart(2, '0');
        return `${py}-${pm}` === filterPeriod;
      });
    }

    // 2. Filter by Budget Code (NEW)
    if (filterBudgetCode) {
      result = result.filter(p => p.budgetCode === filterBudgetCode);
    }

    // 3. Filter by Search
    const q = search.trim().toLowerCase();
    if (q) {
      result = result.filter((p) => {
        const name = (p.nameSnapshot || "").toLowerCase();
        const position = (p.positionSnapshot || p.staff?.jobTitle || p.staff?.role || "").toLowerCase();
        const location = (p.locationSnapshot || "").toLowerCase();
        const status = (p.payment?.status || "").toLowerCase();
        // --- CHANGE: Also search by Budget Code ---
        const budgetCode = (p.budgetCode || "").toLowerCase(); 
        return (
          name.includes(q) ||
          position.includes(q) ||
          location.includes(q) ||
          status.includes(q) ||
          budgetCode.includes(q)
        );
      });
    }
    return result;
  }, [payrollArray, search, filterPeriod, filterBudgetCode]); // --- CHANGE: Add filterBudgetCode dependency

  useEffect(() => setCurrentPage(1), [search, filterPeriod, filterBudgetCode, payrolls]);

  const paginatedPayrolls = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredPayrolls.slice(start, start + itemsPerPage);
  }, [filteredPayrolls, currentPage]);

  const totalPages = Math.ceil(filteredPayrolls.length / itemsPerPage);

  // --- Export Logic ---
  // --- CHANGE 3: Add Budget Code to Exports ---
  const mapPayrollToExportRow = (p) => ({
    "Employee Name": p.nameSnapshot || "",
    Position: p.positionSnapshot || p.staff?.jobTitle || p.staff?.role || "",
    Location: p.locationSnapshot || "",
    Period: `${p.payrollPeriod?.month || "-"}/${p.payrollPeriod?.year || "-"}`,
    "Budget Code": p.budgetCode || "", // NEW
    "Worked Hours": p.workedHours || 0,
    "Gross Pay": p.grossPay || 0,
    Deductible: p.deductibleAmount || 0,
    "PIT (20%)": p.pit || 0,
    NSI: p.nsiAmount || 0,
    Hardship: p.hardship || 0,
    Advance: p.advance || 0,
    Arrears: p.arrears || 0,
    "Medical Allocated": p.medicalAllocated || 0,
    "Net Salary": p.netSalary || 0,
    "Prepared By": p.approvalProcess?.preparedBy 
      ? `${p.approvalProcess.preparedBy.firstName} ${p.approvalProcess.preparedBy.lastName}` 
      : "No",
    "Checked By": p.approvalProcess?.checkedBy 
      ? `${p.approvalProcess.checkedBy.firstName} ${p.approvalProcess.checkedBy.lastName}` 
      : "No",
    "Approved By": p.approvalProcess?.approvedBy 
      ? `${p.approvalProcess.approvedBy.firstName} ${p.approvalProcess.approvedBy.lastName}` 
      : "No",
    "Payment Status": p.payment?.status || "Unknown",
    "Payment Date": formatDate(p.payment?.paymentDate),
  });

  const exportToExcel = () => {
    if (!filteredPayrolls.length) return alert("No payroll data to export!");
    const exportData = filteredPayrolls.map(mapPayrollToExportRow);
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Payroll");
    worksheet["!cols"] = Object.keys(exportData[0]).map((col) => ({
      wch: Math.max(
        col.length,
        ...exportData.map((row) => (row[col] ? row[col].toString().length : 0))
      ) + 2,
    }));
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), "payroll_export.xlsx");
  };

  const exportToCSV = () => {
    if (!filteredPayrolls.length) return alert("No payroll data to export!");
    const worksheet = XLSX.utils.json_to_sheet(filteredPayrolls.map(mapPayrollToExportRow));
    const csv = XLSX.utils.sheet_to_csv(worksheet);
    saveAs(new Blob([csv], { type: "text/csv;charset=utf-8;" }), "payroll_export.csv");
  };

  const exportToPDF = () => {
    if (!filteredPayrolls.length) return alert("No payroll data to export!");
    const exportData = filteredPayrolls.map(mapPayrollToExportRow);
    const doc = new jsPDF("l", "pt", "a4");
    const columns = Object.keys(exportData[0]);
    
    doc.setFontSize(18);
    doc.setTextColor(237, 125, 49); 
    doc.text("Payroll Export", 40, 40);
    
    autoTable(doc, {
      startY: 60,
      head: [columns],
      body: exportData.map((row) => columns.map((col) => row[col])),
      styles: { fontSize: 7, cellPadding: 2 },
      headStyles: { fillColor: [237, 125, 49] },
      theme: "striped",
      margin: { left: 10, right: 10 },
      didDrawPage: (data) => {
        const pageCount = doc.internal.getNumberOfPages();
        doc.setFontSize(10);
        doc.setTextColor(150);
        doc.text(`Page ${doc.internal.getCurrentPageInfo().pageNumber} of ${pageCount}`, data.settings.margin.left, doc.internal.pageSize.height - 10);
      },
    });
    doc.save("payroll_export.pdf");
  };

  const handleExport = (format) => {
    setExportDropdownOpen(false);
    switch (format) {
      case "excel": return exportToExcel();
      case "csv": return exportToCSV();
      case "pdf": return exportToPDF();
      default: alert("Unsupported export format");
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setExportDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (id) => {
    if (selected.includes(id)) setSelected(selected.filter((x) => x !== id));
    else setSelected([...selected, id]);
  };

  const handleBulkAction = async (actionType) => {
    if (selected.length === 0) return;
    if (actionType === 'reject') {
      setShowBulkReject(true);
      return;
    }

    setLoading(true);
    setLoadingAction(actionType);
    
    try {
      const promises = selected.map(async (id) => {
        if (actionType === 'pay') return markPayrollPaid(id);
        if (actionType === 'prepare') return preparePayroll(id);
        if (actionType === 'check') return checkPayroll(id);
        if (actionType === 'approve') return approvePayroll(id, { comments: "Bulk approved" });
      });

      await Promise.all(promises);
      setFeedbackMsg(`${selected.length} records successfully ${actionType}ed.`);
      setSelected([]);
      setTimeout(() => setFeedbackMsg(""), 3000);
      refresh();
    } catch (error) {
      console.error(`Bulk ${actionType} error:`, error);
      alert(`Failed to complete bulk action: ${error.message}`);
    } finally {
      setLoading(false);
      setLoadingAction(null);
    }
  };

  const handleConfirmBulkReject = async () => {
    if (!bulkRejectReason.trim()) {
      alert("Please provide a reason for rejection.");
      return;
    }
    setLoading(true);
    try {
      const promises = selected.map(id => rejectPayroll(id, { reason: bulkRejectReason }));
      await Promise.all(promises);
      
      setFeedbackMsg(`${selected.length} records rejected.`);
      setSelected([]);
      setBulkRejectReason("");
      setShowBulkReject(false);
      setTimeout(() => setFeedbackMsg(""), 3000);
      refresh();
    } catch (error) {
      console.error("Bulk reject error:", error);
      alert(`Failed to reject: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSinglePay = async (id) => {
    try {
      setLoading(true);
      await markPayrollPaid(id);
      setFeedbackMsg("Payroll marked as paid successfully.");
      setTimeout(() => setFeedbackMsg(""), 3000);
      refresh();
    } catch (error) {
      console.error("Error paying payroll:", error.message);
      alert("Failed to mark payroll as paid.");
    } finally {
      setLoading(false);
    }
  };

  const getMonthName = (monthNum) => {
    const date = new Date();
    date.setMonth(monthNum - 1);
    return date.toLocaleString('default', { month: 'long' });
  };

  return (
    <div className="mt-8 bg-white rounded shadow p-4 overflow-hidden">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-3">
        <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-center w-full sm:w-auto">
          <h2 className="text-lg font-semibold text-gray-700 whitespace-nowrap">Payroll Management</h2>
          
          <div className="relative">
            <select 
              value={filterPeriod} 
              onChange={(e) => setFilterPeriod(e.target.value)}
              className="appearance-none bg-white border border-gray-300 text-gray-700 py-1.5 pl-3 pr-8 rounded text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              <option value="">All Periods</option>
              {availablePeriods.map(period => {
                const [y, m] = period.split('-');
                return (
                  <option key={period} value={period}>
                    {getMonthName(parseInt(m))} {y}
                  </option>
                )
              })}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
              <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/></svg>
            </div>
          </div>
        </div>

        {selected.length > 0 && (
          <div className="flex flex-wrap gap-2 animate-fade-in-down bg-orange-50 p-2 rounded border border-orange-200 w-full sm:w-auto justify-end items-center">
            <span className="self-center text-xs font-semibold text-orange-800 mr-1">{selected.length} Selected:</span>
            
            <button onClick={() => handleBulkAction('prepare')} disabled={loading} className="text-xs bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 disabled:opacity-50">
              {loadingAction === 'prepare' ? '...' : 'Prepare'}
            </button>
            <button onClick={() => handleBulkAction('check')} disabled={loading} className="text-xs bg-yellow-600 text-white px-3 py-1 rounded hover:bg-yellow-700 disabled:opacity-50">
              {loadingAction === 'check' ? '...' : 'Check'}
            </button>
            <button onClick={() => handleBulkAction('approve')} disabled={loading} className="text-xs bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700 disabled:opacity-50">
              {loadingAction === 'approve' ? '...' : 'Approve'}
            </button>
            <button onClick={() => handleBulkAction('reject')} disabled={loading} className="text-xs bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700 disabled:opacity-50">
              {loadingAction === 'reject' ? '...' : 'Reject'}
            </button>
            <button onClick={() => handleBulkAction('pay')} disabled={loading} className="text-xs bg-orange-600 text-white px-3 py-1 rounded hover:bg-orange-700 disabled:opacity-50">
              {loadingAction === 'pay' ? '...' : 'Pay'}
            </button>

            {showBulkReject && (
              <div className="flex items-center gap-2 bg-white p-1 rounded border border-red-200 animate-fade-in">
                <input 
                  type="text" 
                  placeholder="Rejection reason..." 
                  className="text-xs px-2 py-1 outline-none"
                  value={bulkRejectReason}
                  onChange={(e) => setBulkRejectReason(e.target.value)}
                />
                <button 
                  onClick={handleConfirmBulkReject}
                  disabled={loading}
                  className="text-xs bg-red-700 text-white px-2 py-1 rounded hover:bg-red-800"
                >
                  Confirm
                </button>
                <button 
                  onClick={() => { setShowBulkReject(false); setBulkRejectReason(""); }}
                  className="text-xs text-gray-500 hover:text-gray-700 px-1"
                >
                  ✕
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-3">
        <input
          type="search"
          placeholder="Search by name, position, location or status"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full sm:max-w-xs rounded border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
        />
        <div className="relative inline-block text-left" ref={dropdownRef}>
          <button
            onClick={() => setExportDropdownOpen(!exportDropdownOpen)}
            className="inline-flex justify-center w-full rounded bg-orange-600 px-4 py-2 text-white hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500"
          >
            Export
            <svg className="ml-2 -mr-1 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.25a.75.75 0 01-1.06 0L5.21 8.27a.75.75 0 01.02-1.06z" clipRule="evenodd" />
            </svg>
          </button>
          {exportDropdownOpen && (
            <div className="origin-top-right absolute right-0 mt-2 w-48 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 z-50">
              <div className="py-1">
                {["excel", "csv", "pdf"].map((format) => (
                  <button
                    key={format}
                    onClick={() => handleExport(format)}
                    className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-orange-100 hover:text-orange-900"
                  >
                    Export as {format.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Desktop Table */}
      <div className="hidden md:block overflow-x-auto border rounded-lg">
        <table className="min-w-full text-sm whitespace-nowrap">
          <thead>
            <tr className="bg-orange-100 text-gray-600 text-left">
              <th className="px-2 py-3 w-10 border-r">
                <input 
                  type="checkbox" 
                  checked={paginatedPayrolls.length > 0 && paginatedPayrolls.every(p => selected.includes(p._id))}
                  onChange={(e) => {
                    if(e.target.checked) {
                      setSelected(prev => [...new Set([...prev, ...paginatedPayrolls.map(p => p._id)])]);
                    } else {
                      setSelected(prev => prev.filter(id => !paginatedPayrolls.map(p => p._id).includes(id)));
                    }
                  }}
                />
              </th>
              <th className="px-4 py-3 border-r">Name</th>
              <th className="px-4 py-3 border-r">Position</th>
              <th className="px-4 py-3 border-r">Period</th>
              
              {/* --- CHANGE 4: Add Budget Code Column Header --- */}
              <th className="px-4 py-3 border-r font-mono text-xs text-gray-500">Budget Code</th>

              <th className="px-2 py-3 border-r text-center">Prep</th>
              <th className="px-2 py-3 border-r text-center">Chk</th>
              <th className="px-2 py-3 border-r text-center">Appr</th>
              <th className="px-4 py-3 text-right border-r">Gross</th>
              <th className="px-4 py-3 text-right border-r">Deduct.</th>
              <th className="px-4 py-3 text-right border-r">PIT</th>
              <th className="px-4 py-3 text-right border-r">NSI</th>
              <th className="px-4 py-3 text-right border-r text-gray-500">Hardship</th>
              <th className="px-4 py-3 text-right border-r">Adv.</th>
              <th className="px-4 py-3 text-right border-r text-green-600">Arr.</th>
              <th className="px-4 py-3 text-right border-r text-gray-500">Medical</th>
              <th className="px-4 py-3 text-right border-r font-bold">Net</th>
              <th className="px-4 py-3 border-r">Paid Date</th>
              <th className="px-4 py-3 border-r">Status</th>
              <th className="px-4 py-3">Action</th>
            </tr>
          </thead>
          <tbody>
            {/* --- CHANGE 5: Update colSpan to 20 because we added 1 column --- */}
            {paginatedPayrolls.length === 0 ? (
              <tr>
                <td colSpan="20" className="text-center text-gray-500 py-4">No payroll records found.</td>
              </tr>
            ) : paginatedPayrolls.map((p) => {
              const payment = p.payment || { status: "unpaid" };
              const isPaid = payment.status === "paid";
              const isApproved = !!p.approvalProcess?.approvedBy; 
              const isRejected = !!p.approvalProcess?.rejectedBy;
              
              const canPay = isApproved && !isPaid;
              
              return (
                <tr key={p._id} className={`border-b hover:bg-gray-50 ${selected.includes(p._id) ? 'bg-orange-50' : ''}`}>
                  <td className="px-2 py-2 border-r">
                    <input
                      type="checkbox"
                      disabled={loading}
                      checked={selected.includes(p._id)}
                      onChange={() => handleSelect(p._id)}
                      className="rounded text-orange-600 focus:ring-orange-500"
                    />
                  </td>
                  <td className="px-4 py-2 font-medium text-gray-900 border-r">{p.nameSnapshot}</td>
                  <td className="px-4 py-2 text-gray-600 border-r">
                    {p.positionSnapshot || p.staff?.jobTitle || p.staff?.role || 'N/A'}
                  </td>
                  <td className="px-4 py-2 text-gray-600 border-r text-center">
                    {p.payrollPeriod?.month || '-'}/{p.payrollPeriod?.year || '-'}
                  </td>

                  {/* --- CHANGE 6: Add Budget Code Cell --- */}
                  <td className="px-4 py-2 border-r font-mono text-xs text-blue-700 bg-blue-50 text-center">
                    {p.budgetCode || '-'}
                  </td>

                  <td className="px-2 py-2 border-r text-center">
                    <span title={`Prepared by: ${p.approvalProcess?.preparedBy?.firstName || 'N/A'}`} className={`inline-block w-6 h-6 rounded-full text-xs flex items-center justify-center ${p.approvalProcess?.preparedBy ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-400'}`}>
                      {getUserInitials(p.approvalProcess?.preparedBy)}
                    </span>
                  </td>
                  <td className="px-2 py-2 border-r text-center">
                    <span title={`Checked by: ${p.approvalProcess?.checkedBy?.firstName || 'N/A'}`} className={`inline-block w-6 h-6 rounded-full text-xs flex items-center justify-center ${p.approvalProcess?.checkedBy ? 'bg-yellow-100 text-yellow-600' : 'bg-gray-100 text-gray-400'}`}>
                      {getUserInitials(p.approvalProcess?.checkedBy)}
                    </span>
                  </td>
                  <td className="px-2 py-2 border-r text-center">
                    <span title={`Approved by: ${p.approvalProcess?.approvedBy?.firstName || 'N/A'}`} className={`inline-block w-6 h-6 rounded-full text-xs flex items-center justify-center ${p.approvalProcess?.approvedBy ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'}`}>
                      {getUserInitials(p.approvalProcess?.approvedBy)}
                    </span>
                  </td>

                  <td className="px-4 py-2 text-right border-r">{formatCurrency(p.grossPay)}</td>
                  <td className="px-4 py-2 text-right border-r text-gray-500">{formatCurrency(p.deductibleAmount)}</td>
                  <td className="px-4 py-2 text-right border-r text-red-600">({formatCurrency(p.pit)})</td>
                  <td className="px-4 py-2 text-right border-r text-red-600">({formatCurrency(p.nsiAmount)})</td>
                  <td className="px-4 py-2 text-right border-r text-gray-400">{formatCurrency(p.hardship)}</td>
                  <td className="px-4 py-2 text-right border-r text-red-600">({formatCurrency(p.advance)})</td>
                  <td className="px-4 py-2 text-right border-r text-green-600">+{formatCurrency(p.arrears)}</td>
                  <td className="px-4 py-2 text-right border-r text-gray-400">{formatCurrency(p.medicalAllocated)}</td>
                  <td className="px-4 py-2 text-right border-r font-bold text-gray-800">{formatCurrency(p.netSalary)}</td>
                  
                  <td className="px-4 py-2 text-right border-r text-xs text-gray-500">
                    {formatDate(p.payment?.paymentDate)}
                  </td>

                  <td className="px-4 py-2 border-r capitalize">
                    <span className={`${
                        isPaid ? 'text-green-600 bg-green-100' : 
                        isRejected ? 'text-red-600 bg-red-100' : 
                        'text-orange-600 bg-orange-100'
                      } px-2 py-1 rounded-full text-xs font-semibold`}>
                      {isRejected ? 'Rejected' : payment.status}
                    </span>
                  </td>
                  <td className="px-4 py-2">
                    {isPaid ? (
                      <span className="text-gray-400 italic text-xs">Paid</span>
                    ) : isRejected ? (
                      <span className="text-red-400 italic text-xs">Rejected</span>
                    ) : !canPay ? (
                      <span className="text-gray-300 italic text-xs" title="Must be Approved first">Pending</span>
                    ) : (
                      <button
                        onClick={() => handleSinglePay(p._id)}
                        className="bg-orange-500 text-white px-3 py-1 rounded text-sm hover:bg-orange-600 transition-colors"
                        disabled={loading}
                      >
                        {loading ? "..." : "Pay"}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile Card Layout */}
      <div className="md:hidden space-y-4">
        {paginatedPayrolls.length === 0 ? (
          <p className="text-center text-gray-500">No payroll records found.</p>
        ) : paginatedPayrolls.map((p) => {
            const payment = p.payment || { status: "unpaid" };
            const isPaid = payment.status === "paid";
            const isApproved = !!p.approvalProcess?.approvedBy;
            const isRejected = !!p.approvalProcess?.rejectedBy;
            const canPay = isApproved && !isPaid;
            const positionDisplay = p.positionSnapshot || p.staff?.jobTitle || p.staff?.role || 'N/A';

            return (
              <div key={p._id} className="border rounded-lg p-4 shadow-sm bg-white">
                <div className="flex justify-between items-start mb-3">
                    <div className="flex items-center gap-2">
                        <input
                        type="checkbox"
                        disabled={loading}
                        checked={selected.includes(p._id)}
                        onChange={() => handleSelect(p._id)}
                        className="rounded text-orange-600 focus:ring-orange-500"
                        />
                        <span className="font-semibold text-gray-900">{p.nameSnapshot}</span>
                    </div>
                    <div className="text-right">
                        <span className={`capitalize ${
                            isPaid ? 'text-green-600 bg-green-100' : 
                            isRejected ? 'text-red-600 bg-red-100' : 
                            'text-orange-600 bg-orange-100'
                        } px-2 py-1 rounded-full text-xs font-semibold block mb-1`}>
                            {isRejected ? 'Rejected' : payment.status}
                        </span>
                        <span className="text-xs bg-gray-100 px-2 py-1 rounded text-gray-600 block">
                            {p.payrollPeriod?.month || '-'}/{p.payrollPeriod?.year || '-'}
                        </span>
                    </div>
                </div>
                
                {/* --- CHANGE 7: Add Budget Code to Mobile Card --- */}
                {p.budgetCode && (
                    <div className="mb-2 flex items-center gap-2">
                        <span className="text-[10px] font-bold text-gray-500 uppercase">Budget:</span>
                        <span className="text-xs font-mono text-blue-700 bg-blue-50 px-2 py-0.5 rounded border border-blue-100">
                            {p.budgetCode}
                        </span>
                    </div>
                )}

                <div className="grid grid-cols-3 gap-2 text-xs mb-3 text-center">
                     <div className={`p-1 rounded border ${p.approvalProcess?.preparedBy ? 'border-blue-200 bg-blue-50' : 'border-gray-200 bg-gray-100'}`}>
                        <div className="font-semibold text-blue-800">Prep</div>
                        <div className="text-[10px]">{getUserInitials(p.approvalProcess?.preparedBy)}</div>
                     </div>
                     <div className={`p-1 rounded border ${p.approvalProcess?.checkedBy ? 'border-yellow-200 bg-yellow-50' : 'border-gray-200 bg-gray-100'}`}>
                        <div className="font-semibold text-yellow-800">Chk</div>
                        <div className="text-[10px]">{getUserInitials(p.approvalProcess?.checkedBy)}</div>
                     </div>
                     <div className={`p-1 rounded border ${p.approvalProcess?.approvedBy ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-100'}`}>
                        <div className="font-semibold text-green-800">Appr</div>
                        <div className="text-[10px]">{getUserInitials(p.approvalProcess?.approvedBy)}</div>
                     </div>
                </div>

                <div className="grid grid-cols-2 gap-y-2 gap-x-2 text-sm mb-3">
                    <div className="col-span-2 text-gray-500 text-xs border-b pb-1 mb-1">Financial Breakdown</div>
                    <div className="text-gray-600">Gross:</div>
                    <div className="text-right font-medium">{formatCurrency(p.grossPay)}</div>
                    <div className="text-red-600">PIT:</div>
                    <div className="text-right text-red-600">({formatCurrency(p.pit)})</div>
                    <div className="text-red-600">NSI:</div>
                    <div className="text-right text-red-600">({formatCurrency(p.nsiAmount)})</div>
                    <div className="text-red-600">Advance:</div>
                    <div className="text-right text-red-600">({formatCurrency(p.advance)})</div>
                    <div className="text-green-600">Arrears:</div>
                    <div className="text-right text-green-600">+{formatCurrency(p.arrears)}</div>
                    <div className="text-gray-400">Hardship:</div>
                    <div className="text-right text-gray-400">{formatCurrency(p.hardship)}</div>
                    <div className="text-gray-400">Medical:</div>
                    <div className="text-right text-gray-400">{formatCurrency(p.medicalAllocated)}</div>
                    <div className="col-span-2 border-t pt-1 mt-1 flex justify-between items-center">
                        <span className="font-bold">Net Salary:</span>
                        <span className="font-bold text-gray-900">{formatCurrency(p.netSalary)}</span>
                    </div>
                </div>

                <div className="border-t pt-2 mt-2 flex justify-end">
                  {isPaid ? (
                    <span className="text-gray-400 italic text-xs w-full text-right">Processed</span>
                  ) : isRejected ? (
                    <span className="text-red-400 italic text-xs w-full text-right">Rejected</span>
                  ) : !canPay ? (
                     <span className="text-gray-300 italic text-xs w-full text-right">Pending Approval</span>
                  ) : (
                    <button
                      onClick={() => handleSinglePay(p._id)}
                      className="bg-orange-500 text-white px-4 py-2 rounded text-sm hover:bg-orange-600 w-full"
                      disabled={loading}
                    >
                      Pay Now
                    </button>
                  )}
                </div>
              </div>
            );
        })}
      </div>

      {totalPages > 1 && (
        <div className="mt-6 flex justify-center gap-2 flex-wrap">
          <button 
            onClick={() => setCurrentPage(p => Math.max(1, p-1))} 
            disabled={currentPage===1} 
            className={`rounded px-3 py-1 ${currentPage===1?'bg-gray-300 cursor-not-allowed':'bg-orange-100 hover:bg-orange-200 text-gray-800'}`}
          >
            Prev
          </button>
          {[...Array(totalPages)].map((_, i) => (
            <button 
              key={i} 
              onClick={() => setCurrentPage(i+1)} 
              className={`rounded px-3 py-1 ${currentPage===i+1?'bg-orange-600 text-white font-semibold':'bg-gray-200 hover:bg-gray-300 text-gray-800'}`}
            >
              {i+1}
            </button>
          ))}
          <button 
            onClick={() => setCurrentPage(p => Math.min(totalPages, p+1))} 
            disabled={currentPage===totalPages} 
            className={`rounded px-3 py-1 ${currentPage===totalPages?'bg-gray-300 cursor-not-allowed':'bg-orange-100 hover:bg-orange-200 text-gray-800'}`}
          >
            Next
          </button>
        </div>
      )}
      
      {feedbackMsg && <div className="mt-4 text-sm text-center text-green-600 font-medium bg-green-50 py-2 rounded">{feedbackMsg}</div>}
    </div>
  );
};

export default PayrollTable;